import { useForm } from "react-hook-form";
import {
    IManufacturerPriceSetting
} from "../../../models/PriceSettings";
import { TextInput } from "../../shared/Inputs/TextInput";
import SelectInput from "../../shared/Inputs/SelectInput";
import GeneralPriceSettingSingle from "../PriceConfiguration/GeneralPriceSettingSingle";
import { ButtonInput } from "../../shared/Inputs/ButtonInput";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import useManufacturerPriceSettingSingle from "../../../data/useManufacturerPriceSettingSingle";
import { useEffect, useState } from "react";

import useUkvd from "../../../data/useUkvd";
import ComboBoxInput from "../../shared/Inputs/ComboBoxInput";

interface IManufacturerPriceSettingParams {
    customerPriceConfigurationId: string;
    priceSetting?: IManufacturerPriceSetting;
    onSuccess?: any;
}

export default function ManufacturerPriceSingle({
    customerPriceConfigurationId,
    priceSetting,
    onSuccess,
}: IManufacturerPriceSettingParams) {
    const { makes } = useUkvd();
    const [vehicleMakes, setVehicleMakes] = useState<any[]>([]);
    const { manufacturerPriceSetting, update, create, deleteSetting } = useManufacturerPriceSettingSingle({ manufacturerPriceSettingId: priceSetting?.id });
    const { saveHandlers } = useDefaultCRUDHandlers("Manufacturer Price Setting");
    const [isLoading, setIsLoading] = useState(true);
    const [bandError, setBandError] = useState<string | null>(null);
    const { handleSubmit, register, setValue, watch, reset } = useForm<IManufacturerPriceSetting>({
        defaultValues: {
            id: priceSetting?.id || "",
            customerPriceConfigurationId: customerPriceConfigurationId,
            value: priceSetting?.value || null,
            toValue: priceSetting?.toValue || null,
            year: priceSetting?.year || null,
            make: priceSetting?.make || "",

            priceSetting: {
                value: priceSetting?.priceSetting?.value || 0,
                isFixed: priceSetting?.priceSetting?.isFixed || true,
            },
        },
    });

    // Blank inputs mean "no bound", so they have to reach the API as null rather than "".
    const blankToNull = (val: any) => (val === "" || val === null || val === undefined ? null : val);

    const cleanData = (data: any) => {
        let isFixed = data.priceSetting.isFixed;
        if (isFixed === "true" || isFixed === "") isFixed = true;
        else if(isFixed === "false") isFixed = false;
        return {
            ...data,
            value: blankToNull(data.value),
            toValue: blankToNull(data.toValue),
            year: blankToNull(data.year) === 0 ? null : blankToNull(data.year),
            priceSetting: {
                ...data.priceSetting,
                isFixed: isFixed
            }
        }
    }

    const onSubmit = (data: any) => {
        const from = blankToNull(data.value);
        const to = blankToNull(data.toValue);

        if (from !== null && to !== null && Number(to) <= Number(from)) {
            setBandError("The 'To' value must be higher than the 'From' value.");
            return;
        }
        setBandError(null);

        if (priceSetting){
            update.mutate(cleanData(data), {
                ...saveHandlers,
                onSuccess: () => {
                    saveHandlers.onSuccess();
                    onSuccess();
                }
            });
        }else{
            create.mutate(cleanData(data), {
                ...saveHandlers,
                onSuccess: () => {
                    saveHandlers.onSuccess();
                    onSuccess();
                }
            });
        }
    }

    const onRemove = () => {
        if (priceSetting){
            // alert("Not yet implemented");
            deleteSetting.mutate(priceSetting.id, {
                ...saveHandlers,
                onSuccess: () => {
                    saveHandlers.onSuccess();
                    onSuccess();
                }
            });
        }
    }

    useEffect(() => {
        // console.log(priceSetting);
        // console.log(isLoading);
        if (priceSetting && isLoading) {
            reset(priceSetting);

            setIsLoading(false);
        } else if (!priceSetting && isLoading) {
            setIsLoading(false);
        }
    }, [priceSetting]);

    useEffect(() => {
        if (makes.isLoading || !makes.data) return;

        setVehicleMakes(makes.data?.data.map((make, index) => {
            return { id: index, name: make.make };
        }));

    }, []);

    
    if (isLoading) return null;

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col space-y-4">
            <div className="flex flex-col lg:flex-row items-center gap-x-4">      
                {priceSetting && priceSetting.make == "Base" ? <></> :
                    // <TextInput 
                    //     label="Make" 
                    //     register={register} 
                    //     registerName="make" 
                    //     placeholder="Audi, BMW, Ford etc."
                    //     cols="w-64" 
                    // />
                    <ComboBoxInput
                        label="Make"
                        register={register}
                        registerName="make"
                        options={vehicleMakes}
                        setValue={setValue}
                        searchField={"name"}
                        valueField={"name"}
                        initialValue={priceSetting?.make ?? ""}
                    />
                }  
                <TextInput
                    label={"Trade Poor From (£)"}
                    registerName={`value`}
                    register={register}
                    placeholder="No minimum"
                    cols="w-48"
                />
                <TextInput
                    label={"Trade Poor To (£)"}
                    registerName={`toValue`}
                    register={register}
                    placeholder="No maximum"
                    cols="w-48"
                />
                <TextInput
                    label={"Minimum Year"}
                    registerName={`year`}
                    register={register}
                    placeholder="-"
                    cols="w-40"
                />

                <span className="pt-8 mr-[-10px]">-</span>
                <GeneralPriceSettingSingle
                    label={"Price Deduction"}
                    objectName={"priceSetting"}
                    register={register}
                    setValue={setValue}
                    watch={watch}
                    minVal={null}
                />

                <div className="flex grow mt-[28px] space-x-4">
                    <ButtonInput label={priceSetting ? "Save" : "Create"} isSubmit />
                    {priceSetting && priceSetting.make != "Base" && (
                        <ButtonInput
                        label="Delete"
                        isSubmit={false}
                        onClick={onRemove}
                        classes="bg-red-600 hover:bg-red-600/80"
                        />
                    )}
                </div>
            </div>

            {bandError && (
                <p className="text-sm text-red-600">{bandError}</p>
            )}

            <p className="text-xs text-gray-500">
                Bands run from just above the "From" value up to and including the "To" value, so
                consecutive bands can share a number without leaving a gap — e.g. 1500 to 2000, then
                2000 to 3000, then 3000 with no maximum. Leave a bound blank for no limit.
            </p>
        </form>
    )

}